import type React from "react"
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"
import type { GridSortModel } from "@mui/x-data-grid-pro"
import usePersistentContext from "../../hooks/usePersistentContext"
import {
  isRelative,
  safelyParseDate,
  type DynamicDateRange,
} from "../../utils/dates"
import {
  DEFAULT_GDELT_SORT_MODEL,
  DEFAULT_NWSS_SORT_MODEL,
  DEFAULT_PRECOMPUTED_PAGE_SIZE,
} from "./constants"
import {
  GDELT_SORTABLE_FIELDS,
  NWSS_SORTABLE_FIELDS,
} from "./PrecomputedSortableColumns"
import { parseStoredSortModel } from "./parseStoredSortModel"
import {
  DEFAULT_GDELT_DATE_RANGE,
  DEFAULT_NWSS_DATE_RANGE,
} from "./precomputedDateRangeConstants"
import {
  ALL_DISEASES,
  ALL_GEO,
  type PrecomputedBiothreatSignal,
  type PrecomputedBiothreatSignalsState,
  type PrecomputedGdeltFilters,
  type PrecomputedNwssFilters,
  type PrecomputedScanMode,
  type PrecomputedTab,
} from "./types"

const DEFAULT_GDELT_FILTERS: PrecomputedGdeltFilters = {
  disease: ALL_DISEASES,
  state: ALL_GEO,
  country: ALL_GEO,
  impactScoreMin: "",
  titleSearch: "",
  memberTitleSearch: "",
  summarySearch: "",
}

const DEFAULT_NWSS_FILTERS: PrecomputedNwssFilters = {
  disease: ALL_DISEASES,
  state: ALL_GEO,
}

const parseStoredDateRange = (raw: string): DynamicDateRange | null => {
  try {
    const parsed = JSON.parse(raw) as DynamicDateRange
    if (isRelative(parsed)) {
      return parsed
    }
    if (Array.isArray(parsed)) {
      const [start, end] = parsed
      const startDate = safelyParseDate(start)
      const endDate = safelyParseDate(end)
      if (
        startDate?.isValid() &&
        endDate?.isValid() &&
        !startDate.isAfter(endDate)
      ) {
        return [startDate, endDate]
      }
    }
  } catch {
    return null
  }
  return null
}

/** Keep a persisted value only when it's actually a string, else the default. */
const asString = (value: unknown, fallback: string): string =>
  typeof value === "string" ? value : fallback

/**
 * Parse persisted filters into the canonical shape. Builds only known fields
 * (so stale keys from older schemas — e.g. a legacy `dateRange` — are dropped,
 * not re-persisted) and coerces every field to a string default unless the
 * stored value is genuinely a string, so a `null`, missing, or wrong-typed
 * value can never reach a downstream `.trim()` / API param. Falls back to
 * defaults on malformed JSON, matching `parseStoredDateRange` /
 * `parseStoredSortModel` (which likewise validate, not just cast).
 */
const parseStoredGdeltFilters = (value: string): PrecomputedGdeltFilters => {
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>
    return {
      disease: asString(parsed.disease, DEFAULT_GDELT_FILTERS.disease),
      state: asString(parsed.state, DEFAULT_GDELT_FILTERS.state),
      country: asString(parsed.country, DEFAULT_GDELT_FILTERS.country),
      impactScoreMin: asString(
        parsed.impactScoreMin,
        DEFAULT_GDELT_FILTERS.impactScoreMin
      ),
      titleSearch: asString(
        parsed.titleSearch,
        DEFAULT_GDELT_FILTERS.titleSearch
      ),
      memberTitleSearch: asString(
        parsed.memberTitleSearch,
        DEFAULT_GDELT_FILTERS.memberTitleSearch
      ),
      summarySearch: asString(
        parsed.summarySearch,
        DEFAULT_GDELT_FILTERS.summarySearch
      ),
    }
  } catch {
    return DEFAULT_GDELT_FILTERS
  }
}

const parseStoredNwssFilters = (value: string): PrecomputedNwssFilters => {
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>
    return {
      disease: asString(parsed.disease, DEFAULT_NWSS_FILTERS.disease),
      state: asString(parsed.state, DEFAULT_NWSS_FILTERS.state),
    }
  } catch {
    return DEFAULT_NWSS_FILTERS
  }
}

const PrecomputedBiothreatSignalsContext = createContext<
  PrecomputedBiothreatSignalsState | undefined
>(undefined)

export const usePrecomputedBiothreatSignals =
  (): PrecomputedBiothreatSignalsState => {
    const context = useContext(PrecomputedBiothreatSignalsContext)
    if (!context) {
      throw new Error(
        "usePrecomputedBiothreatSignals must be used within PrecomputedBiothreatSignalsProvider"
      )
    }
    return context
  }

export const PrecomputedBiothreatSignalsProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [storedTab, setStoredTab] = usePersistentContext<PrecomputedTab>([
    "precomputed-biothreat-tab",
  ])
  const activeTab = storedTab ?? "gdelt"
  const setActiveTab = useCallback(
    (tab: PrecomputedTab) => {
      void setStoredTab(tab)
    },
    [setStoredTab]
  )

  const [storedSelectedExpanded, setStoredSelectedExpanded] =
    usePersistentContext<boolean>(["precomputed-selected-signals-expanded"], {
      serialize: String,
      deserialize: value => value === "true",
    })
  const selectedSignalsExpanded = storedSelectedExpanded ?? false
  const setSelectedSignalsExpanded = useCallback(
    (expanded: boolean) => {
      void setStoredSelectedExpanded(expanded)
    },
    [setStoredSelectedExpanded]
  )

  const [storedPageSize, setStoredPageSize] = usePersistentContext<number>(
    ["precomputed-page-size"],
    { serialize: String, deserialize: value => Number(value) }
  )
  const pageSize = storedPageSize ?? DEFAULT_PRECOMPUTED_PAGE_SIZE
  const setPageSize = useCallback(
    (next: number) => {
      void setStoredPageSize(next)
    },
    [setStoredPageSize]
  )

  const [storedMapRailWidth, setStoredMapRailWidth] =
    usePersistentContext<number>(["precomputed-map-rail-width"], {
      serialize: String,
      deserialize: value => Number(value),
    })
  const mapRailWidth = storedMapRailWidth ?? null
  const setMapRailWidth = useCallback(
    (width: number) => {
      void setStoredMapRailWidth(width)
    },
    [setStoredMapRailWidth]
  )

  const [storedScanMode, setStoredScanMode] =
    usePersistentContext<PrecomputedScanMode>(["precomputed-scan-mode"])
  const scanMode = storedScanMode ?? "auto"
  const setScanMode = useCallback(
    (mode: PrecomputedScanMode) => {
      void setStoredScanMode(mode)
    },
    [setStoredScanMode]
  )

  const [storedGdeltDateRange, setStoredGdeltDateRange] =
    usePersistentContext<DynamicDateRange>(["precomputed-gdelt-date-range"], {
      serialize: JSON.stringify,
      deserialize: value =>
        parseStoredDateRange(value) ?? DEFAULT_GDELT_DATE_RANGE,
    })

  const [storedNwssDateRange, setStoredNwssDateRange] =
    usePersistentContext<DynamicDateRange>(["precomputed-nwss-date-range"], {
      serialize: JSON.stringify,
      deserialize: value =>
        parseStoredDateRange(value) ?? DEFAULT_NWSS_DATE_RANGE,
    })

  const [storedGdeltFilters, setStoredGdeltFilters] =
    usePersistentContext<PrecomputedGdeltFilters>(
      ["precomputed-gdelt-filters"],
      {
        serialize: JSON.stringify,
        deserialize: parseStoredGdeltFilters,
      }
    )

  const [storedNwssFilters, setStoredNwssFilters] =
    usePersistentContext<PrecomputedNwssFilters>(["precomputed-nwss-filters"], {
      serialize: JSON.stringify,
      deserialize: parseStoredNwssFilters,
    })

  const [storedGdeltSortModel, setStoredGdeltSortModel] =
    usePersistentContext<GridSortModel>(["precomputed-gdelt-sort"], {
      serialize: JSON.stringify,
      deserialize: value =>
        parseStoredSortModel(
          value,
          GDELT_SORTABLE_FIELDS,
          DEFAULT_GDELT_SORT_MODEL
        ),
    })

  const [storedNwssSortModel, setStoredNwssSortModel] =
    usePersistentContext<GridSortModel>(["precomputed-nwss-sort"], {
      serialize: JSON.stringify,
      deserialize: value =>
        parseStoredSortModel(
          value,
          NWSS_SORTABLE_FIELDS,
          DEFAULT_NWSS_SORT_MODEL
        ),
    })

  const gdeltDateRange = storedGdeltDateRange ?? DEFAULT_GDELT_DATE_RANGE
  const nwssDateRange = storedNwssDateRange ?? DEFAULT_NWSS_DATE_RANGE

  const dateRange = activeTab === "nwss" ? nwssDateRange : gdeltDateRange

  const setDateRange = useCallback(
    (range: DynamicDateRange) => {
      if (activeTab === "nwss") {
        void setStoredNwssDateRange(range)
      } else {
        void setStoredGdeltDateRange(range)
      }
    },
    [activeTab, setStoredGdeltDateRange, setStoredNwssDateRange]
  )

  const gdeltFilters = storedGdeltFilters ?? DEFAULT_GDELT_FILTERS
  const setGdeltFilters = useCallback(
    (filters: PrecomputedGdeltFilters) => {
      void setStoredGdeltFilters(filters)
    },
    [setStoredGdeltFilters]
  )
  const resetGdeltFilters = useCallback(() => {
    void setStoredGdeltFilters(DEFAULT_GDELT_FILTERS)
    void setStoredGdeltDateRange(DEFAULT_GDELT_DATE_RANGE)
    void setStoredGdeltSortModel(DEFAULT_GDELT_SORT_MODEL)
  }, [setStoredGdeltFilters, setStoredGdeltDateRange, setStoredGdeltSortModel])

  const nwssFilters = storedNwssFilters ?? DEFAULT_NWSS_FILTERS
  const setNwssFilters = useCallback(
    (filters: PrecomputedNwssFilters) => {
      void setStoredNwssFilters(filters)
    },
    [setStoredNwssFilters]
  )
  const resetNwssFilters = useCallback(() => {
    void setStoredNwssFilters(DEFAULT_NWSS_FILTERS)
    void setStoredNwssDateRange(DEFAULT_NWSS_DATE_RANGE)
    void setStoredNwssSortModel(DEFAULT_NWSS_SORT_MODEL)
  }, [setStoredNwssFilters, setStoredNwssDateRange, setStoredNwssSortModel])

  const gdeltSortModel = storedGdeltSortModel ?? DEFAULT_GDELT_SORT_MODEL
  const setGdeltSortModel = useCallback(
    (sortModel: GridSortModel) => {
      void setStoredGdeltSortModel(sortModel)
    },
    [setStoredGdeltSortModel]
  )

  const nwssSortModel = storedNwssSortModel ?? DEFAULT_NWSS_SORT_MODEL
  const setNwssSortModel = useCallback(
    (sortModel: GridSortModel) => {
      void setStoredNwssSortModel(sortModel)
    },
    [setStoredNwssSortModel]
  )

  const [pinnedSignals, setPinnedSignals] = useState<
    Map<string, PrecomputedBiothreatSignal>
  >(new Map())

  const addPinnedSignal = useCallback((signal: PrecomputedBiothreatSignal) => {
    setPinnedSignals(prev => new Map(prev).set(signal.id, signal))
  }, [])

  const removePinnedSignal = useCallback((signalId: string) => {
    setPinnedSignals(prev => {
      const next = new Map(prev)
      next.delete(signalId)
      return next
    })
  }, [])

  const value = useMemo(
    (): PrecomputedBiothreatSignalsState => ({
      selectedSignalsExpanded,
      setSelectedSignalsExpanded,
      pageSize,
      setPageSize,
      mapRailWidth,
      setMapRailWidth,
      scanMode,
      setScanMode,
      activeTab,
      setActiveTab,
      dateRange,
      setDateRange,
      gdeltFilters,
      setGdeltFilters,
      resetGdeltFilters,
      nwssFilters,
      setNwssFilters,
      resetNwssFilters,
      gdeltSortModel,
      setGdeltSortModel,
      nwssSortModel,
      setNwssSortModel,
      pinnedSignals,
      addPinnedSignal,
      removePinnedSignal,
    }),
    [
      selectedSignalsExpanded,
      setSelectedSignalsExpanded,
      pageSize,
      setPageSize,
      mapRailWidth,
      setMapRailWidth,
      scanMode,
      setScanMode,
      activeTab,
      setActiveTab,
      dateRange,
      setDateRange,
      gdeltFilters,
      setGdeltFilters,
      resetGdeltFilters,
      nwssFilters,
      setNwssFilters,
      resetNwssFilters,
      gdeltSortModel,
      setGdeltSortModel,
      nwssSortModel,
      setNwssSortModel,
      pinnedSignals,
      addPinnedSignal,
      removePinnedSignal,
    ]
  )

  return (
    <PrecomputedBiothreatSignalsContext.Provider value={value}>
      {children}
    </PrecomputedBiothreatSignalsContext.Provider>
  )
}
